import React, { useEffect, useId, useState, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import dayjs from "dayjs";
import { Flex, Spin } from "antd";
import AuthService from "../../Services/AuthService";
import PatientService from "../../Services/PatientService";
import HeaderPrescription from "../MedicalRecords/MyPrescription/HeaderPrescription";
import FooterPrescription from "../MedicalRecords/MyPrescription/FooterPrescription";
import { HSdecrypt } from "../../utils/encryptPassword";
import Odontogram from "../MedicalRecords/MyPrescription/Odontogram";

const ViewPrescriptionHS = ({ prescription, selectedPatient }) => {
  const contentRef = useRef();
  const [doctorData, setDoctorData] = useState([]);
  const [doctorSign, setDoctorSign] = useState(null);
  const [doctorStamp, setDoctorStamp] = useState(null);
  const [hospitalLogo, setHospitalLogo] = useState(null);
  const [selectedPatientDetails, setSelectedPatientDetails] = useState([]);
  const [clinicDetails, setClinicDetails] = useState(null);
  const [hospitalAccountData, setHospitalAccountData] = useState(null);
  // const activePatient = JSON.parse(sessionStorage.getItem("patientData"));
  // const patientCode = activePatient?.patientId;
  const [age, setAge] = useState();
  const [gender, setGender] = useState();

  const [values, setValues] = useState(Array(32).fill(""));

  const fetchPrescriptionDetails = () => {
    if (
      !prescription?.dentalDetails &&
      prescription?.consultationId &&
      prescription?.userId === "DZU5051209"
    ) {
      const dentalPayload = {
        consultationId: prescription.consultationId,
        status: 1,
      };

      PatientService.getDentalFormData(dentalPayload)
        .then((res) => {
          const dentalData = res?.data?.[0] || {};
          const filteredVal = Array.from(
            { length: 32 },
            (_, i) => dentalData[`c${i + 1}`] || "",
          );
          setValues(filteredVal);
        })
        .catch((e) => console.error(e));
    } else if (prescription?.dentalDetails) {
      const filteredVal = Array.from(
        { length: 32 },
        (_, i) => prescription.dentalDetails[`c${i + 1}`] || "",
      );
      setValues(filteredVal);
    }
  };

  useEffect(() => {
    if (prescription?.userId === "DZU5051209") {
      fetchPrescriptionDetails();
    }
  }, [prescription]);

  useEffect(() => {
    const fetchData = async () => {
      if (!prescription?.userId) return;
      try {
        const payload = {
          userId: prescription?.userId,
          status: 1,
        };

        const [stampRes, userRes] = await Promise.all([
          AuthService.getHSUserData(payload).catch((err) => {
            console.error("Error fetching HS stamp/signature:", err);
            return null;
          }),
          AuthService.getUser(prescription?.userId).catch((err) => {
            console.error("Error fetching user data:", err);
            return null;
          }),
        ]);

        const userObj = userRes?.data?.[0] || {};
        const stampObj = stampRes?.data || {};

        const mergedDoctorData = {
          ...stampObj,
          ...userObj,
          headerDetails: userObj?.headerDetails || stampObj?.headerDetails,
          userSalutation: stampObj?.userSalutation || userObj?.salutation,
          userFirstName: stampObj?.userFirstName || userObj?.firstName,
          userLastName: stampObj?.userLastName || userObj?.lastName,
          qualification: userObj?.qualification || stampObj?.qualification,
          speciality: userObj?.speciality || stampObj?.speciality,
          registrationNumber:
            userObj?.registrationNumber || stampObj?.registrationNumber,
        };

        setDoctorData(mergedDoctorData);

        if (stampObj?.doctorSignatureB64) {
          setDoctorSign(
            "data:image/jpeg;base64," + stampObj.doctorSignatureB64,
          );
        } else if (userObj?.doctorSignatureName) {
          setDoctorSign(
            `${process.env.REACT_APP_IMAGE_URL}${userObj.doctorSignatureName}`,
          );
        }

        if (stampObj?.doctorStampB64) {
          setDoctorStamp(
            "data:image/jpeg;base64," + stampObj.doctorStampB64,
          );
        } else if (userObj?.doctorStampName) {
          setDoctorStamp(
            `${process.env.REACT_APP_IMAGE_URL}${userObj.doctorStampName}`,
          );
        }

        const clinicId =
          prescription?.clinic_Id ||
          userObj?.clinic_Id ||
          stampObj?.clinic_Id;

        const accountId =
          prescription?.account_Id ||
          userObj?.account_Id ||
          stampObj?.account_Id;

        if (clinicId) fetchClinicData(clinicId);
        if (accountId) fetchHospitalData(accountId);
      } catch (error) {
        console.error("error", error);
      }
    };
    fetchData();
  }, [prescription?.userId, prescription?.clinic_Id, prescription?.account_Id]);

  useEffect(() => {
    let payload = {
      patientId: selectedPatient?.patientId,
    };
    PatientService.HSPatientData(payload)
      .then((result) => {
        setSelectedPatientDetails(result.data);
        setGender(result.data[0].gender);
        const dobYear = result.data[0].dob.slice(6);
        const currYear = new Date().getFullYear();
        if (dobYear) {
          const patientAge = currYear - Number(dobYear);
          setAge(patientAge);
        }
      })
      .catch((error) => {
        console.error("Error fetching patient list:", error);
      });
  }, [selectedPatient?.patientId]);

  const patientName = `${selectedPatientDetails[0]?.firstName || ""} ${
    selectedPatientDetails[0]?.lastName || ""
  }`.trim();

  const patientName1 = `${selectedPatientDetails[0]?.firstName || ""} ${
    selectedPatientDetails[0]?.lastName || ""
  }`.trim();

  const formattedDate = (() => {
    const d = prescription?.consultationDate?.slice(0, 11).split("-");
    return d?.length === 3 ? `${d[2].trim()}-${d[1]}-${d[0]}` : "";
  })();

  const patientGender =
    selectedPatientDetails?.[0]?.gender ||
    selectedPatientDetails?.gender ||
    gender;

  const patientMobile =
    selectedPatientDetails?.[0]?.mobile ||
    selectedPatientDetails?.mobile;

  const patientInfo = {
    patientName:
      selectedPatient?.patientName ||
      `${selectedPatient?.firstName ?? ""} ${selectedPatient?.lastName ?? ""}`.trim() ||
      patientName,
    age: age
      ? `${age}${patientGender ? ` / ${patientGender}` : ""}`
      : "",
    date:
      formattedDate ||
      (prescription?.consultationDate
        ? dayjs(prescription.consultationDate).format("DD-MM-YYYY")
        : ""),
    patientId:
      selectedPatient?.patientId ||
      prescription?.patientId ||
      selectedPatientDetails?.[0]?.patientId ||
      selectedPatientDetails?.patientId,
    mobile: patientMobile,
  };

  // useEffect(() => {
  //   AuthService.getUser(prescription?.userId).then((result) => {
  //     // setDoctorData(result.data[0]);
  //     fetchClinicData(result.data[0]?.clinic_Id);
  //     fetchHospitalData(result.data[0]?.account_Id);
  //   });
  // }, []);

  const fetchHospitalData = async (id) => {
    try {
      const payload = {
        account_Id: id,
        status: 1,
      };
      const response = await AuthService.getHSHospitalAccountDataLogo(payload);
      let decryptedData = HSdecrypt(response.data.data);
      if (typeof decryptedData === "string") {
        decryptedData = JSON.parse(decryptedData);
      }
      setHospitalAccountData(decryptedData);
      setHospitalLogo("data:image/jpeg;base64," + decryptedData?.logoB64);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchClinicData = async (AccId) => {
    try {
      const response = await PatientService.getHSClinicData(AccId);
      let decryptedData = HSdecrypt(response.data.data);
      if (typeof decryptedData === "string") {
        decryptedData = JSON.parse(decryptedData);
      }
      setClinicDetails(decryptedData[0]);
    } catch (error) {
      console.error(error);
    }
  };

  // const download = () => {
  //   const input = document.getElementById(getId);
  //   const pdf = new jsPDF({
  //     orientation: "portrait",
  //     unit: "px",
  //     format: "a4",
  //     userUnit: "px",
  //   });
  //   pdf
  //     .html(input, {
  //       html2canvas: { scale: 0.5, allowTaint: false },
  //       margin: [1, 1, 1, 1],
  //       autoPaging: true,
  //     })
  //     .then(() => {
  //       const fileName = "e-Prescription-" + +".pdf";
  //       pdf.save(fileName);
  //     });
  // };

  const download = () => {
    const input = contentRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: true,
      onclone: (clonedDoc) => {
        const imgElement = clonedDoc.querySelector("img");
        imgElement.crossOrigin = "anonymous";
      },
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: "a4",
          userUnit: "px",
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

        const width = imgWidth * ratio;
        const height = imgHeight * ratio;

        pdf.addImage(imgData, "PNG", 0, 0, width, height);
        pdf.save("Prescription-" + patientName1 + ".pdf");
      })
      .catch((error) => {
        console.error("Error capturing the content:", error);
      });
  };

  return (
    <section>
      {!clinicDetails ||
      !hospitalAccountData ||
      !selectedPatientDetails ||
      !doctorData ? (
        <div className="min-h-[570px] flex items-center justify-center mt-[10%]">
          <Flex align="center" gap="middle">
            <Spin size="large" tip="Loading" />
          </Flex>
        </div>
      ) : (
        <>
          <div
            ref={contentRef}
            className="w-full mb-4 border rounded overflow-x-auto md:py-4 py-2 md:px-4 px-2"
          >
            <div className="px-2">
              <HeaderPrescription
                clinicDetails={clinicDetails}
                doctorData={doctorData}
                hospitalAccountData={hospitalAccountData}
                patientInfo={patientInfo}
              />

              {/* <div className="border-b-2 border-gray-300 mt-3"></div> */}

              <div className="md:flex justify-end text-sm font-bold gap-1 mt-3">
                Date:
                <span className="ml-1 text-sm font-medium">
                  {dayjs(prescription?.consultationDate).format("DD-MM-YYYY")}
                </span>
              </div>

              {/* <div className="mb-3 mt-5"> */}
              <div className="flex sm:flex-col gap-3 my-2">
                <div className="text-sm font-bold flex gap-1">
                  Name:
                  <span className="text-sm text-gray-500 font-semibold">
                    {patientName}
                  </span>
                </div>
                <div className="text-sm font-bold md:flex gap-1">
                  Age/Gender:
                </div>
                <span className="text-sm text-gray-500 font-semibold">
                  {age ? age : "NA"}/
                  {gender?.trim().toUpperCase() === "M" ? "Male" : "Female"}
                </span>
                <div className="text-sm font-bold md:flex gap-1">
                  Phone Number:
                  <span className="text-sm text-gray-500 font-semibold">
                    {selectedPatientDetails[0]?.mobile}
                  </span>
                </div>
                <div className="text-sm font-bold flex gap-1 ">
                  Visit Type:
                  <span className="text-sm text-gray-500 font-semibold">
                    {prescription?.consultationType}
                  </span>
                </div>
              </div>
              {/* </div> */}
              {/* Vitals part */}
              <div className="border-b border-gray-300"></div>
              <div className="flex gap-3 my-3 flex-wrap justify-stretch">
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "BPS",
                )?.value &&
                prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "BPD",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">BP:</label>
                    <span className="text-gray-500 text-sm">
                      {`${
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "BPS",
                        )?.value
                      }/${
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "BPD",
                        )?.value
                      }`}{" "}
                      mmHg
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "PO",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">Pulse Ox:</label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "PO",
                        )?.value
                      }{" "}
                      %
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "HR",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">Heart Rate:</label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "HR",
                        )?.value
                      }{" "}
                      Beats/min
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "RR",
                )?.value ? (
                  <div className="flex gap-1 md:w-[30%]">
                    <label className="font-medium text-sm">
                      Respiration Rate:
                    </label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "RR",
                        )?.value
                      }{" "}
                      Breaths/min
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "TP",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">Temperature:</label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "TP",
                        )?.value
                      }{" "}
                      °F
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "HI",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">Height:</label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "HI",
                        )?.value
                      }{" "}
                      cm
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "WE",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">Weight:</label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "WE",
                        )?.value
                      }{" "}
                      kg
                    </span>
                  </div>
                ) : null}
                {prescription?.patientVitals.find(
                  (vital) => vital.vitalCode === "BMI",
                )?.value ? (
                  <div className="flex gap-1 md:w-[20%]">
                    <label className="font-medium text-sm">BMI:</label>
                    <span className="text-gray-500 text-sm">
                      {
                        prescription?.patientVitals.find(
                          (vital) => vital.vitalCode === "BMI",
                        )?.value
                      }{" "}
                      Kg/m<sup>2</sup>
                    </span>
                  </div>
                ) : null}
              </div>

              <hr className="hr-line mt-3" />

              <div className="grid grid-cols-1 my-2">
                {prescription?.consultationReason && (
                  <p>
                    <b className="">Chief Complaint: </b>
                    {prescription?.consultationReason}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 my-2">
                {prescription?.symptoms && (
                  <p>
                    <b className="">Symptoms: </b>
                    {prescription?.symptoms}
                  </p>
                )}
              </div>
              {prescription?.patientDiagnosisDetails?.filter(
                (diagnosis) => diagnosis.diagnosisType === "Primary",
              )?.length !== 0 && (
                <div className="grid grid-cols-1 my-2">
                  <p className="">
                    <b className="">Primary Diagnosis:</b>
                  </p>
                  <ol className="list-decimal ml-5">
                    {prescription?.patientDiagnosisDetails
                      ?.filter(
                        (diagnosis) => diagnosis?.diagnosisType === "Primary",
                      )
                      ?.map((primaryDiagnosis) => (
                        <li>
                          <span>{primaryDiagnosis?.diagnosisDescription}</span>
                          <span className="font-bold">
                            ({primaryDiagnosis?.diagnosisCode})
                          </span>
                        </li>
                      ))}
                  </ol>
                </div>
              )}
              {prescription?.patientDiagnosisDetails?.filter(
                (diagnosis) => diagnosis?.diagnosisType === "Secondary",
              )?.length !== 0 && (
                <div className="grid grid-cols-1 my-2">
                  <p className="">
                    <b className="">Secondary Diagnosis:</b>
                  </p>
                  <ol className="list-decimal ml-5">
                    {prescription?.patientDiagnosisDetails
                      ?.filter(
                        (diagnosis) => diagnosis?.diagnosisType === "Secondary",
                      )
                      ?.map((secondaryDiagnosis) => (
                        <li>
                          <span>
                            {secondaryDiagnosis?.diagnosisDescription}
                          </span>
                          <span className="font-bold">
                            ({secondaryDiagnosis?.diagnosisCode})
                          </span>
                        </li>
                      ))}
                  </ol>
                </div>
              )}
              {prescription?.specialityDetails?.length !== 0 && (
                <div className="mt-2  mb-5">
                  <p className="mb-2">
                    <b className="font-bold">Clinical Notes:</b>
                  </p>

                  <div className="md:w-[60%] w-full">
                    <div>
                      {prescription?.specialityDetails &&
                        prescription?.specialityDetails?.map(
                          (template, idx) => (
                            <div
                              key={idx}
                              className={`${
                                template?.componentDetailsList?.length !== 0
                                  ? "block"
                                  : "hidden"
                              }`}
                            >
                              {/* <p>
                                <b className="font-semibold border p-1.5 rounded-lg bg-brand-lightBg">
                                  {template.templateName}
                                </b>
                              </p> */}
                              <div className="w-full flex items-center gap-2 flex-wrap">
                                {template?.componentDetailsList.map(
                                  (formFields, index) => (
                                    <div
                                      key={index}
                                      className="text-sm flex items-center gap-1"
                                    >
                                      <p className="font-medium w-max my-1 py-1">
                                        {formFields.componentLabel}
                                      </p>
                                      {": "}
                                      <p className="text-gray-700 w-max my-1 py-1">
                                        {formFields.componentValue}
                                      </p>
                                      {template?.componentDetailsList?.length -
                                        1 !==
                                        index && (
                                        <span className="font-medium">|</span>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          ),
                        )}
                    </div>
                  </div>
                </div>
              )}

              {prescription?.userId == "DZU5051209" && (
                <div className="mb-2 overflow-scroll w-full scrollWidth">
                  <p className="mb-2">
                    <b className="font-bold">Clinical Notes: </b>
                  </p>
                  <div className=" w-full">
                    <Odontogram
                      values={values}
                      setValues={setValues}
                      editTrue={true}
                    />
                  </div>
                </div>
              )}

              <div className="">
                {prescription?.drugPrescription && (
                  <div>
                    <p className="mb-2">
                      <b className="">Medicines:</b>
                    </p>
                    <table className="table-auto w-full mb-5">
                      <thead>
                        <tr className="border-b-2 border-t-2 border-gray-300">
                          <th className="dark:border-slate-700 px-3 py-2 mb-2 text-left">
                            Medicines
                          </th>
                          <th className="dark:border-slate-700 px-3 py-2 mb-2 text-left">
                            Dosage
                          </th>
                          <th className="dark:border-slate-700 px-3 py-2 mb-2 text-left">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {prescription?.drugPrescription?.map(
                          (medicine, idx) => (
                            <tr
                              key={idx}
                              className="border-b-2 border-gray-300"
                            >
                              {/* <td className="px-3 py-1">{idx + 1}</td> */}
                              <td className="px-3 py-1">{medicine.drugName}</td>
                              <td className=" px-3 py-1">
                                <p>{medicine.dosage}</p>
                                <p>
                                  (
                                  {medicine?.afterBeforeFood === "AF"
                                    ? "After Food"
                                    : "Before Food"}
                                  )
                                </p>
                              </td>
                              <td className="px-3 py-1">
                                {medicine.duration} days
                                <p>
                                  {medicine?.notes?.length > 20
                                    ? `${medicine?.instruction?.slice(
                                        0,
                                        20,
                                      )}...`
                                    : medicine?.instruction}
                                </p>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {prescription?.labPrescription && (
                  <div className="mt-2  mb-5">
                    <p className="  mb-2">
                      <b className="">Lab Test:</b>
                    </p>

                    <div>
                      <div className=""></div>
                      <div className=" ">
                        {prescription?.labPrescription?.map((lab, idx) => (
                          <div
                            key={idx}
                            className="text-sm  border dark:border-slate-700 px-3 py-1"
                          >
                            {lab.labTestName}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="my-2">
                <div className="w-6/6  my-2">
                  {prescription?.recommendation && (
                    <div className="mb-5">
                      <p>
                        <b className=" mr-3">Recommendation:</b>
                        <span className="">{prescription?.recommendation}</span>
                      </p>
                    </div>
                  )}

                  {prescription?.followupVisitDate && (
                    <div>
                      <p className=" my-2">
                        <b className=" mr-1">Scheduled follow up visit date:</b>
                        {dayjs(prescription?.followupVisitDate).format(
                          "DD-MM-YYYY",
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <div className="flex items-center mt-3">
                  {doctorStamp && (
                    <img
                      src={doctorStamp}
                      alt="Stamp"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                      width={120}
                    />
                  )}
                </div>

                <div className="">
                  {doctorSign && (
                    <div className="mb-2">
                      <img
                        src={doctorSign}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                        alt="Signature"
                        width={100}
                      />
                      <p className="font-rubik text-gray-primary text-sm">
                        {doctorData?.userSalutation} {doctorData?.userFirstName}{" "}
                        {doctorData?.userLastName}
                      </p>
                      <p className="font-rubik text-gray-primary text-sm">
                        {doctorData?.qualification}
                      </p>
                      <p className="font-rubik text-gray-primary text-sm">
                        {doctorData?.speciality}
                      </p>
                      <p className="font-rubik text-gray-primary text-sm">
                        Regn No. {doctorData?.registrationNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <FooterPrescription
              clinicDetails={clinicDetails}
              doctorData={doctorData}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => download()}
              className="text-white bg-primaryColor w-44 h-12  rounded-lg "
            >
              Download Prescription
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default ViewPrescriptionHS;
